import { WidgetProps } from '@rjsf/utils';
import { useMemo } from 'react';
import { SectionTitle } from 'components/form/SectionTitle';
import { AddCircle, Edit } from '@mui/icons-material';
import { PillIconButton } from 'components/button/PillIconButton';
import useDialog from '../../hooks/useDialog.tsx';
import PromptAdd from '../../views/prompts/PromptAdd.tsx';
import { useParams } from 'react-router';
import ViewDialog from '../dialog/ViewDialog.tsx';
import usePrompts from '../../api/prompts/usePrompts.ts';
import Loader from '../Loader.tsx';
import PromptEdit from '../../views/prompts/PromptEdit.tsx';
import ListSelectAutocomplete from './ListSelectAutocomplete.tsx';
import PromptRenderer from './CustomRenderers/PromptRenderer.tsx';
import { FormBlockContext } from './FormBlock.tsx';
import { useSearchParams } from 'react-router-dom';
import { Prompt } from 'api/model.ts';

type PromptWidgetOptions = {
  inputsAllowed?: boolean;
  requiredInputs?: string[];
  additionalInputsAllowed?: boolean;
  templateHint?: string;
};

const filterPrompt = (prompt: Prompt, options: PromptWidgetOptions) => {
  if (options.inputsAllowed === false && prompt.inputs.length > 0) {
    return false;
  }

  if (options.requiredInputs) {
    const hasAllRequiredInputs = options.requiredInputs.every((input) => prompt.inputs.includes(input));
    if (!hasAllRequiredInputs) {
      return false;
    }
    if (!options.additionalInputsAllowed) {
      const hasOnlyRequiredInputs = prompt.inputs.every((input) => options.requiredInputs?.includes(input));
      if (!hasOnlyRequiredInputs) {
        return false;
      }
    }
  }
  return true;
};

const PromptWidget = (props: WidgetProps<PromptWidgetOptions>) => {
  const options = props.options as PromptWidgetOptions;
  const { projectId = '', workflowId = '' } = useParams();
  const [showAddPromptDialog, AddPromptDialog] = useDialog(ViewDialog);
  const [showEditPromptDialog, EditPromptDialog] = useDialog(ViewDialog);
  const [_, setSearchParams] = useSearchParams();

  const contextPrompts = (props.formContext as FormBlockContext | undefined)?.prompts;
  const { prompts: fetchedPrompts, isFetching } = usePrompts(projectId, true, false, !contextPrompts);
  const prompts = contextPrompts ?? fetchedPrompts;

  const promptsById = useMemo(() => new Map((prompts ?? []).map((prompt) => [prompt.id, prompt])), [prompts]);

  const promptsOptions = useMemo(
    () =>
      prompts
        ?.filter((prompt) => filterPrompt(prompt, options))
        .map((prompt) => ({
          value: prompt.prompt_name,
          const: prompt.id,
          group: prompt.folder_path?.trim() || undefined,
        })) ?? [],
    [prompts, options.requiredInputs],
  );

  const handleShowAddPrompt = () => {
    showAddPromptDialog(true);
  };

  const handleShowEditPrompt = () => {
    showEditPromptDialog(true);
  };

  const handleCreateNewPrompt = (promptId: string) => {
    props.onChange(promptId);
    showAddPromptDialog(false);
  };

  const handleCloseDialog = () => {
    setSearchParams({});
  };

  return (
    <>
      <SectionTitle title="Prompt settings" />
      <div className="flex w-100 flex-row items-center gap-1.5 mt-6">
        {!isFetching ? (
          <ListSelectAutocomplete
            options={promptsOptions}
            value={props.value}
            onChange={props.onChange}
            label={props.schema.title}
            required={props.required}
            disabled={props.disabled || props.readonly || isFetching}
            render={(promptId) => <PromptRenderer prompt={promptsById.get(promptId)} fallback={promptId} />}
          />
        ) : (
          <Loader />
        )}

        <PillIconButton
          title="Edit prompt"
          className="ms-1"
          onClick={handleShowEditPrompt}
          disabled={!props.value || isFetching || props.disabled}
          color="info"
        >
          <Edit />
        </PillIconButton>
        <PillIconButton
          color="info"
          className="ms-1"
          title="Add prompt"
          disabled={isFetching || props.disabled}
          onClick={handleShowAddPrompt}
        >
          <AddCircle />
        </PillIconButton>
      </div>
      <AddPromptDialog>
        <PromptAdd
          projectId={projectId ?? ''}
          requiredInputs={options.requiredInputs}
          additionalInputsAllowed={options.additionalInputsAllowed}
          templateHint={options.templateHint}
          onSubmit={handleCreateNewPrompt}
          onCancel={() => showAddPromptDialog(false)}
        />
      </AddPromptDialog>
      <EditPromptDialog onCancel={handleCloseDialog}>
        <PromptEdit
          onSubmit={handleCreateNewPrompt}
          promptId={props.value}
          projectId={projectId}
          activeWorkflowId={workflowId}
          requiredInputs={options.requiredInputs}
          additionalInputsAllowed={options.additionalInputsAllowed}
          templateHint={options.templateHint}
          onCancel={() => showEditPromptDialog(false)}
        />
      </EditPromptDialog>
    </>
  );
};
export default PromptWidget;
